'use client';

import { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';

interface WishlistItem {
  _id: string;
  productId: string;
  productName: string;
  productSlug: string;
  image: string;
  price: number;
  originalPrice?: number;
  stockAvailable: number;
  isAvailable: boolean;
  selectedAttributes?: Record<string, string>;
  addedAt: Date;
}

interface WishlistContextType {
  items: WishlistItem[];
  itemCount: number;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  addToWishlist: (productId: string, selectedAttributes?: Record<string, string>) => Promise<void>;
  removeFromWishlist: (itemId: string) => Promise<void>;
  clearWishlist: () => Promise<void>;
  refreshWishlist: (force?: boolean) => Promise<void>;
  isInWishlist: (productId: string, selectedAttributes?: Record<string, string>) => boolean;
  findWishlistItem: (productId: string, selectedAttributes?: Record<string, string>) => WishlistItem | undefined;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

const getApiUrl = () => process.env.NEXT_PUBLIC_API_URL || 'http://localhost:5000';

async function fetchWishlist(endpoint: string, options: RequestInit = {}) {
  const response = await fetch(`${getApiUrl()}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });

  if (response.status === 404 || response.status === 401) {
    return { items: [] };
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return await response.json();
}

function attributesMatch(
  a?: Record<string, string>,
  b?: Record<string, string>
): boolean {
  const aKeys = Object.keys(a || {});
  const bKeys = Object.keys(b || {});
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => (a as Record<string, string>)[key] === (b || {})[key]);
}

export function WishlistProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [itemCount, setItemCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastFetch = useRef<number>(0);
  const fetchInProgress = useRef<boolean>(false);
  const isMounted = useRef<boolean>(true);
  const itemOperations = useRef<Set<string>>(new Set());

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const refreshWishlist = useCallback(async (force = false) => {
    const timeSinceLastFetch = Date.now() - lastFetch.current;
    if (!force && timeSinceLastFetch < 1000) return;
    if (fetchInProgress.current) return;

    try {
      fetchInProgress.current = true;
      setIsRefreshing(true);
      setError(null);
      lastFetch.current = Date.now();

      const data = await fetchWishlist('/api/wishlist');
      if (!isMounted.current) return;

      const responseItems = data.items || data.data?.items || [];
      setItems(responseItems);
      setItemCount(responseItems.length);
    } catch (err) {
      if (isMounted.current) {
        setError(err instanceof Error ? err.message : 'Failed to load wishlist');
        setItems([]);
        setItemCount(0);
      }
    } finally {
      if (isMounted.current) {
        setIsRefreshing(false);
        setIsLoading(false);
      }
      fetchInProgress.current = false;
    }
  }, []);

  useEffect(() => {
    refreshWishlist();
  }, [refreshWishlist]);

  useEffect(() => {
    const handleLogin = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { wishlistMerged } = customEvent.detail || {};
      console.log('🔔 Login event detected - refreshing wishlist, merged:', wishlistMerged);
      setTimeout(() => refreshWishlist(true), 500);
    };
    window.addEventListener('userLogin', handleLogin);
    return () => window.removeEventListener('userLogin', handleLogin);
  }, [refreshWishlist]);

  useEffect(() => {
    const handleLogout = () => {
      setItems([]);
      setItemCount(0);
      setError(null);
      setTimeout(() => refreshWishlist(true), 500);
    };
    window.addEventListener('userLogout', handleLogout);
    return () => window.removeEventListener('userLogout', handleLogout);
  }, [refreshWishlist]);

  const findWishlistItem = useCallback(
    (productId: string, selectedAttributes?: Record<string, string>) => {
      return items.find(
        (item) =>
          item.productId === productId &&
          attributesMatch(item.selectedAttributes, selectedAttributes)
      );
    },
    [items]
  );

  const isInWishlist = useCallback(
    (productId: string, selectedAttributes?: Record<string, string>): boolean => {
      return !!findWishlistItem(productId, selectedAttributes);
    },
    [findWishlistItem]
  );

  const addToWishlist = async (
    productId: string,
    selectedAttributes?: Record<string, string>
  ) => {
    try {
      setError(null);
      const response = await fetchWishlist('/api/wishlist/add', {
        method: 'POST',
        body: JSON.stringify({ productId, selectedAttributes }),
      });
      if (!isMounted.current) return;
      await refreshWishlist(true);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add to wishlist';
      if (isMounted.current) setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const removeFromWishlist = async (itemId: string) => {
    if (itemOperations.current.has(itemId)) return;

    try {
      itemOperations.current.add(itemId);

      // Optimistic update
      const updatedItems = items.filter((item) => item._id !== itemId);
      if (isMounted.current) {
        setItems(updatedItems);
        setItemCount(updatedItems.length);
      }

      await fetchWishlist(`/api/wishlist/remove/${itemId}`, { method: 'DELETE' });

      setTimeout(() => refreshWishlist(true).catch(console.error), 500);
    } catch (err) {
      // Roll back on failure
      await refreshWishlist(true);
      if (isMounted.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to remove from wishlist';
        setError(errorMessage);
      }
      throw err;
    } finally {
      itemOperations.current.delete(itemId);
    }
  };

  const clearWishlist = async () => {
    try {
      setError(null);
      await fetchWishlist('/api/wishlist/clear', { method: 'DELETE' });
      if (isMounted.current) {
        setItems([]);
        setItemCount(0);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to clear wishlist';
      if (isMounted.current) setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  return (
    <WishlistContext.Provider
      value={{
        items,
        itemCount,
        isLoading,
        isRefreshing,
        error,
        addToWishlist,
        removeFromWishlist,
        clearWishlist,
        refreshWishlist,
        isInWishlist,
        findWishlistItem,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (!context) throw new Error('useWishlist must be used within WishlistProvider');
  return context;
}